// crewbit-admin/src/components/money/AuthoringClientSection.tsx
//
// Spec: §InvoiceAuthoring.7.2 Step 1 (admin parallel). Section card with
// ClientPicker + walk-in toggle + inline name/email/phone fields.
//
// Mirrors crewbit-mobile/components/money/authoring/Step1ClientSelect.tsx.
// Quote-conversion affordance hidden v1 per §3b — DEFERRED until quote-list
// source ships (filed §InvoiceAuthoring.QuoteConversionPicker).

import { ClientPicker, type ClientSelection } from '../common/ClientPicker';
import { palette, radii, spacing, typography } from '../../styles/tokens';
import type { AuthoringDraftState } from '../../hooks/useInvoiceDraft';
import { useClientDefaults } from '../../hooks/useClientDefaults';
import { lookupErrorCopy, type MoneyErrorCode } from '../../lib/errorMap';

function addDaysIso(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

interface Props {
  state: AuthoringDraftState;
  onChange: (patch: Partial<AuthoringDraftState>) => void;
}

export function AuthoringClientSection({ state, onChange }: Props) {
  const fetchDefaults = useClientDefaults();
  // Walk-in mode is derived: no clientId AND at least one walk-in snapshot
  // field is non-null (toggle-on seeds clientName='' so the row qualifies).
  const isWalkIn =
    state.clientId === null &&
    (state.clientName !== null || state.clientEmail !== null || state.clientPhone !== null);

  const clearClientDefaults = () => {
    onChange({
      clientDefaults: {
        resolved: null,
        sources: null,
        reasoning: null,
        isLoading: false,
        error: null,
      },
    });
  };

  const onClientSelect = async (sel: ClientSelection | null) => {
    if (sel) {
      onChange({
        clientId: sel.clientId,
        clientName: sel.name,
        clientEmail: sel.email,
        clientPhone: sel.phone,
        clientDefaults: {
          resolved: null,
          sources: null,
          reasoning: null,
          isLoading: true,
          error: null,
        },
      });
      const result = await fetchDefaults({ clientId: sel.clientId });
      if (result.success) {
        const { resolved, sources, reasoning } = result.data;
        // Pre-fill due_date from client's payment_terms_days resolution
        // (Decision §T2UI.PhaseD-mobile.D2 — pre-fill at client-pick boundary).
        const nextDueDate = addDaysIso(state.issueDate, resolved.payment_terms_days);
        // Auto-append client's notes-for-invoices template iff operator
        // hasn't typed notes yet — otherwise we'd clobber their edits.
        const shouldAutoFillNotes =
          state.notes.trim().length === 0
          && resolved.notes_for_invoices !== null
          && resolved.notes_for_invoices.length > 0;
        onChange({
          dueDate: nextDueDate,
          ...(shouldAutoFillNotes ? { notes: resolved.notes_for_invoices ?? '' } : {}),
          clientDefaults: {
            resolved,
            sources,
            reasoning,
            isLoading: false,
            error: null,
          },
        });
      } else {
        onChange({
          clientDefaults: {
            resolved: null,
            sources: null,
            reasoning: null,
            isLoading: false,
            error: lookupErrorCopy('get_client_defaults', result.error.code as MoneyErrorCode),
          },
        });
      }
    } else {
      onChange({ clientId: null, clientName: null, clientEmail: null, clientPhone: null });
      clearClientDefaults();
    }
  };

  const toggleWalkIn = (on: boolean) => {
    if (on) {
      onChange({ clientId: null, clientName: '', clientEmail: null, clientPhone: null });
    } else {
      onChange({ clientId: null, clientName: null, clientEmail: null, clientPhone: null });
    }
    clearClientDefaults();
  };

  return (
    <section style={CARD}>
      <div style={HEADER_ROW}>
        <div>
          <div style={STEP_LABEL}>Step 1 of 3</div>
          <div style={SECTION_TITLE}>Client</div>
        </div>
      </div>

      {!isWalkIn && (
        <div style={{ marginBottom: spacing.md }}>
          <ClientPicker value={state.clientId} onChange={(sel) => { void onClientSelect(sel); }} allowCreate />
        </div>
      )}

      <label style={TOGGLE_ROW}>
        <span style={TOGGLE_LABEL}>Walk-in / cash sale (no client record)</span>
        <Switch checked={isWalkIn} onChange={toggleWalkIn} />
      </label>

      {isWalkIn && (
        <div style={WALKIN_GROUP}>
          <FieldLabel>Name</FieldLabel>
          <input
            type="text"
            value={state.clientName ?? ''}
            onChange={(e) => onChange({ clientName: e.target.value })}
            placeholder="Customer name"
            autoCapitalize="words"
            style={INPUT}
          />
          <FieldLabel>Email (optional)</FieldLabel>
          <input
            type="email"
            value={state.clientEmail ?? ''}
            onChange={(e) =>
              onChange({ clientEmail: e.target.value.length === 0 ? null : e.target.value })
            }
            placeholder="customer@example.com"
            autoCapitalize="off"
            autoCorrect="off"
            style={INPUT}
          />
          <FieldLabel>Phone (optional)</FieldLabel>
          <input
            type="tel"
            value={state.clientPhone ?? ''}
            onChange={(e) =>
              onChange({ clientPhone: e.target.value.length === 0 ? null : e.target.value })
            }
            placeholder="(555) 555-5555"
            style={INPUT}
          />
        </div>
      )}
    </section>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div style={FIELD_LABEL}>{children}</div>;
}

function Switch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 38,
        height: 22,
        borderRadius: 999,
        border: 'none',
        background: checked ? palette.accent : palette.g200,
        position: 'relative',
        cursor: 'pointer',
        padding: 0,
        transition: 'background 150ms ease',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 18 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: palette.white,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 150ms ease',
        }}
      />
    </button>
  );
}

const CARD: React.CSSProperties = {
  background: palette.white,
  border: `1px solid ${palette.g100}`,
  borderRadius: radii.md,
  padding: spacing.md,
  marginBottom: spacing.md,
};

const HEADER_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: spacing.md,
};

const STEP_LABEL: React.CSSProperties = {
  fontSize: typography.size.micro,
  fontWeight: typography.weight.semibold,
  color: palette.g500,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const SECTION_TITLE: React.CSSProperties = {
  fontSize: typography.size.heading,
  fontWeight: typography.weight.semibold,
  color: palette.g800,
  marginTop: 2,
};

const TOGGLE_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing.md,
  padding: `${spacing.sm}px ${spacing.md}px`,
  background: palette.g50,
  borderRadius: radii.sm,
  border: `1px solid ${palette.g100}`,
  cursor: 'pointer',
};

const TOGGLE_LABEL: React.CSSProperties = {
  fontSize: typography.size.body,
  color: palette.g800,
};

const WALKIN_GROUP: React.CSSProperties = {
  marginTop: spacing.md,
  display: 'flex',
  flexDirection: 'column',
};

const FIELD_LABEL: React.CSSProperties = {
  fontSize: typography.size.caption,
  fontWeight: typography.weight.medium,
  color: palette.g600,
  marginTop: spacing.sm,
  marginBottom: spacing.xxs,
};

const INPUT: React.CSSProperties = {
  background: palette.white,
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.body,
  color: palette.g800,
  fontFamily: typography.fontFamily,
  outline: 'none',
};
